import { useCallback, useMemo, useState } from 'react';

import { isEqual } from 'lodash';

const normalize = <T>(v: unknown): T[] => {
  if (Array.isArray(v)) return v as T[];
  if (v == null || v === '') return [];
  return [v as T];
};

interface UseArrayStateControllerReturn<T> {
  values: T[];
  isSelected: (candidate: T) => boolean;
  toggleOption: (candidate: T) => void;
  removeOption: (candidate: T) => void;
  reset: VoidFunction | undefined;
  setValue: (nextValue: T[]) => void;
}

export const useArrayStateController = <T>({
  value,
  defaultValue = [],
  onChange,
}: {
  value?: T[];
  defaultValue?: T[];
  onChange?: (value: T[]) => void;
}): UseArrayStateControllerReturn<T> => {
  const [internalValue, setInternalValue] = useState<T[]>(defaultValue);

  const isControlled = value !== undefined;
  const values = normalize<T>(isControlled ? value : internalValue);

  const setValue = useCallback(
    (nextValue: T[]) => {
      if (!isControlled) {
        setInternalValue(nextValue);
      }

      onChange?.(nextValue);
    },
    [isControlled, onChange],
  );

  const isSelected = useCallback(
    (candidate: T) => values.some((item) => isEqual(item, candidate)),
    [values],
  );

  const toggleOption = useCallback(
    (candidate: T) => {
      const exists = values.some((item) => isEqual(item, candidate));

      setValue(
        exists
          ? values.filter((item) => !isEqual(item, candidate))
          : [...values, candidate],
      );
    },
    [setValue, values],
  );

  const removeOption = useCallback(
    (candidate: T) => {
      setValue(values.filter((item) => !isEqual(item, candidate)));
    },
    [setValue, values],
  );

  const reset = useMemo(() => {
    return values.length > 0 ? () => setValue([]) : undefined;
  }, [setValue, values]);

  return {
    values,
    isSelected,
    toggleOption,
    removeOption,
    reset,
    setValue,
  };
};
